import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import AppShell from "../components/AppShell";
import SharingTabs from "../components/SharingTabs";
import { CardSkeleton } from "../components/Skeleton";
import {
  deleteShare,
  getTestAccessStatus,
  listShares,
  testShareAccess,
  type ShareRow,
  type TestAccessStatus,
} from "../api/shares";

const POLL_INTERVAL_MS = 30_000;
// Test-access polling — fast cadence because the user is watching a spinner.
// Total budget caps at TEST_ACCESS_TIMEOUT_MS so a stuck task surfaces as
// "no answer" instead of an infinite spinner.
//
// Worst-case lifecycle from click to "succeeded":
//   - up to ~60 s for the prober agent's next heartbeat to pull the task
//   - 1–30 s for Test-Path + Get-ChildItem to return
//   - up to ~60 s for the next heartbeat to report the result back
// Budget 3 minutes to match reality on a heartbeat-paced pipeline.
const TEST_ACCESS_POLL_INTERVAL_MS = 1_500;
const TEST_ACCESS_TIMEOUT_MS = 180_000;

type TestAccessUiState = {
  status: "running" | "ok" | "warn" | "error";
  message: string;
};

export default function Sharing() {
  const [rows, setRows] = useState<ShareRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-share Test Access UI state — tracked here (not inside ShareCard) so
  // the result survives the polling refresh that re-renders the card.
  const [testState, setTestState] = useState<Map<string, TestAccessUiState>>(new Map());

  // True while this component is mounted — consulted by the test-access poll
  // loop so it stops hitting the backend (and stops calling setState) after
  // the user navigates away mid-test.
  const mountedRef = useRef(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchOnce() {
      try {
        const data = await listShares();
        if (!cancelled) {
          setRows(data.results);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(extractError(e));
      }
    }
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      mountedRef.current = false;
    };
  }, []);

  async function onStop(id: string) {
    if (!window.confirm("Stop sharing this folder? Other computers will lose access.")) return;
    try {
      await deleteShare(id);
      setRows((prev) =>
        prev ? prev.map((r) => (r.id === id ? { ...r, status: "removing" } : r)) : prev,
      );
    } catch (e) {
      setError(extractError(e));
    }
  }

  function setShareTestState(shareId: string, state: TestAccessUiState | null) {
    setTestState((prev) => {
      const next = new Map(prev);
      if (state === null) {
        next.delete(shareId);
      } else {
        next.set(shareId, state);
      }
      return next;
    });
  }

  async function onTestAccess(row: ShareRow) {
    setShareTestState(row.id, { status: "running", message: "Checking from another computer…" });
    try {
      const ticket = await testShareAccess(row.id);
      const finalStatus = await pollTestAccessUntilDone(
        ticket.task_id,
        () => mountedRef.current,
      );
      if (!mountedRef.current) return;
      setShareTestState(row.id, interpretTestAccessStatus(finalStatus));
    } catch (e) {
      if (!mountedRef.current || (e instanceof DOMException && e.name === "AbortError")) return;
      setShareTestState(row.id, {
        status: "error",
        message: extractError(e, "Couldn't start the access test."),
      });
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl">
        <h1 className="text-2xl font-semibold text-slate-900">Sharing</h1>
        <p className="mt-1 text-slate-600">
          Share folders and printers with everyone in the office.
        </p>
        <SharingTabs />

        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Shared folders</h2>
            <p className="mt-1 text-sm text-slate-600">
              Folders that anyone in your office can use from any computer.
            </p>
          </div>
          <Link
            to="/sharing/add"
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
          >
            Share a folder
          </Link>
        </div>

        {error ? (
          <p role="alert" className="mt-6 rounded-lg bg-red-50 p-3 text-sm text-red-800">
            {error}
          </p>
        ) : null}

        <div className="mt-8">
          {rows === null ? (
            <CardSkeleton count={2} />
          ) : rows.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="space-y-3">
              {rows.map((row) => (
                <ShareCard
                  key={row.id}
                  row={row}
                  testState={testState.get(row.id) ?? null}
                  onStop={() => onStop(row.id)}
                  onTestAccess={() => onTestAccess(row)}
                  onClearTestState={() => setShareTestState(row.id, null)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function ShareCard({
  row,
  testState,
  onStop,
  onTestAccess,
  onClearTestState,
}: {
  row: ShareRow;
  testState: TestAccessUiState | null;
  onStop: () => void;
  onTestAccess: () => void;
  onClearTestState: () => void;
}) {
  const canTest = row.status === "active";
  const canStop = row.status !== "removing" && row.status !== "removed";
  return (
    <li className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-base font-semibold text-slate-900">
            {row.label || row.share_name}
          </div>
          <div className="mt-1 break-all text-sm text-slate-500">
            on <span className="font-medium">{row.host_agent_label || row.host_agent_hostname}</span>
            {" · "}
            <span className="font-mono">{row.path}</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {row.permissions.map((p) => `${p.principal}: ${p.access}`).join(", ") || "—"}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <StatusBadge status={row.status} />
          {canTest ? (
            <button
              type="button"
              onClick={onTestAccess}
              disabled={testState?.status === "running"}
              className="no-tap-highlight text-xs font-medium text-slate-600 underline hover:text-slate-900 disabled:opacity-50"
            >
              {testState?.status === "running" ? "Checking…" : "Test access"}
            </button>
          ) : null}
          {canStop ? (
            <button
              type="button"
              onClick={onStop}
              className="no-tap-highlight text-xs font-medium text-slate-600 underline hover:text-slate-900"
            >
              Stop sharing
            </button>
          ) : null}
        </div>
      </div>

      {testState ? (
        <TestAccessResult state={testState} onDismiss={onClearTestState} />
      ) : null}
    </li>
  );
}

function StatusBadge({ status }: { status: ShareRow["status"] }) {
  const map: Record<ShareRow["status"], { label: string; className: string }> = {
    pending: { label: "Setting up", className: "bg-amber-100 text-amber-900" },
    active: { label: "Working", className: "bg-emerald-100 text-emerald-900" },
    failed: { label: "Failed", className: "bg-red-100 text-red-900" },
    removing: { label: "Removing", className: "bg-slate-200 text-slate-700" },
    removed: { label: "Removed", className: "bg-slate-200 text-slate-500" },
  };
  const meta = map[status];
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-medium ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
      <h2 className="text-lg font-semibold text-slate-900">No shared folders yet</h2>
      <p className="mt-2 text-sm text-slate-600">
        Click "Share a folder" to make a folder available to everyone in the office.
      </p>
    </div>
  );
}

function extractError(
  e: unknown,
  fallback = "Couldn't load shared folders. We'll try again in a moment.",
): string {
  if (typeof e === "object" && e && "response" in e) {
    const response = (e as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return fallback;
}

// pollTestAccessUntilDone polls the test-access status endpoint until the
// task reaches a terminal state or the overall timeout elapses. Returns
// the final status payload either way so the caller can interpret outcome.
async function pollTestAccessUntilDone(
  taskId: string,
  isAlive?: () => boolean,
): Promise<TestAccessStatus> {
  const deadline = Date.now() + TEST_ACCESS_TIMEOUT_MS;
  let last: TestAccessStatus | null = null;
  while (Date.now() < deadline) {
    if (isAlive && !isAlive()) {
      throw new DOMException("aborted", "AbortError");
    }
    last = await getTestAccessStatus(taskId);
    if (
      last.status === "succeeded" ||
      last.status === "failed" ||
      last.status === "expired" ||
      last.status === "cancelled"
    ) {
      return last;
    }
    await new Promise((resolve) => setTimeout(resolve, TEST_ACCESS_POLL_INTERVAL_MS));
  }
  if (last) return last;
  return {
    task_id: taskId,
    status: "pending",
    result: {},
    error_message: "",
    created_at: "",
    completed_at: null,
  };
}

// interpretTestAccessStatus turns a terminal task + agent-reported outcome
// into the inline-banner state. Copy is plain-English for non-technical
// owners — agent-side "denied / unreachable / not_found" maps to advice
// the owner can act on rather than raw error text.
function interpretTestAccessStatus(status: TestAccessStatus): TestAccessUiState {
  const detail = status.result?.detail ?? "";
  if (status.status !== "succeeded") {
    // Old agents (<0.9.4) don't know about test_share_access; rewrite the
    // "no handler" error to a friendly message during the rollout window.
    if (status.error_message?.includes("no handler for task type")) {
      return {
        status: "error",
        message:
          "This computer is running an older version of KraetdNet. Wait a few minutes for it to update, then try again.",
      };
    }
    const msg =
      status.error_message ||
      "The check didn't run. Make sure another PC is on and try again.";
    return { status: "error", message: msg };
  }
  switch (status.result?.outcome) {
    case "ok":
      return { status: "ok", message: "Another computer opened this folder successfully." };
    case "denied":
      return {
        status: "warn",
        message:
          detail ||
          "The folder is reachable but the other computer doesn't have permission to open it.",
      };
    case "unreachable":
      return {
        status: "error",
        message:
          detail ||
          "The host computer didn't answer. Make sure it's on and connected.",
      };
    case "not_found":
      return {
        status: "error",
        message:
          detail ||
          "The folder name couldn't be found on the host computer. It may have been removed.",
      };
    case "error":
    default:
      return {
        status: "error",
        message: detail || "Something went wrong. Try again in a moment.",
      };
  }
}

function TestAccessResult({
  state,
  onDismiss,
}: {
  state: TestAccessUiState;
  onDismiss: () => void;
}) {
  const styles =
    state.status === "ok"
      ? "bg-emerald-50 text-emerald-900"
      : state.status === "warn"
        ? "bg-amber-50 text-amber-900"
        : state.status === "error"
          ? "bg-red-50 text-red-900"
          : "bg-slate-50 text-slate-700";
  const body = (
    <>
      <div>
        <span className="font-medium">
          {state.status === "ok"
            ? "Access check passed"
            : state.status === "warn"
              ? "Access check warning"
              : state.status === "error"
                ? "Access check failed"
                : "Checking access"}
        </span>{" "}
        — {state.message}
      </div>
      {state.status !== "running" ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss access test result"
          className="no-tap-highlight shrink-0 text-xs font-medium underline hover:opacity-70"
        >
          Dismiss
        </button>
      ) : null}
    </>
  );
  const className = `mt-3 flex items-start justify-between gap-3 rounded-xl px-3 py-2.5 text-sm ${styles}`;
  if (state.status === "error") {
    return (
      <div role="alert" className={className}>
        {body}
      </div>
    );
  }
  return (
    <div role="status" className={className}>
      {body}
    </div>
  );
}
